import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { slugify, type Translatable } from '@hrlabs/shared-types';
import { getRow, saveRow } from '../../lib/db';
import { uploadToStorage } from '../../lib/upload';
import { useToast } from '../../components/Toast';
import { BilingualField } from '../../components/BilingualField';
import { ImageUploadField } from '../../components/ImageUploadField';

const EMPTY: Translatable = { en: '', bg: '' };

export function ServiceEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useToast();
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState<Translatable>({ ...EMPTY });
  const [fullDescription, setFullDescription] = useState<Translatable>({ ...EMPTY });
  const [type, setType] = useState<'for-companies' | 'for-candidates'>('for-companies');
  const [icon, setIcon] = useState('');
  const [imagePath, setImagePath] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);

  useEffect(() => {
    if (!id) return;
    getRow('services', id).then((r) => {
      if (r) {
        setTitle((r.title as Translatable) ?? { ...EMPTY });
        setFullDescription((r.fullDescription as Translatable) ?? { ...EMPTY });
        setType((r.type as 'for-companies' | 'for-candidates') ?? 'for-companies');
        setIcon(String(r.icon ?? ''));
        setImagePath((r.imagePath as string | null) ?? null);
      }
      setLoading(false);
    });
  }, [id]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.bg.trim() || !title.en.trim() || !fullDescription.bg.trim() || !fullDescription.en.trim()) {
      notify('danger', 'Title and description are required in both languages.');
      return;
    }
    setSaving(true);
    try {
      const savedId = await saveRow('services', id, {
        title,
        fullDescription,
        type,
        icon: icon || null,
        slug: slugify(title.en || title.bg),
      });
      if (imageFile) {
        const { path } = await uploadToStorage('service-images', savedId, imageFile);
        await saveRow('services', savedId, { imagePath: path });
      }
      notify('success', 'Service saved.');
      navigate(`/services/${savedId}`);
    } catch (err) {
      notify('danger', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <form onSubmit={onSubmit} style={{ maxWidth: 760 }}>
      <h3 className="fw-bold mb-3">{id ? 'Edit service' : 'New service'}</h3>
      <BilingualField label="Title" value={title} onChange={setTitle} required rows={1} />
      <BilingualField
        label="Full description"
        value={fullDescription}
        onChange={setFullDescription}
        required
        rich
      />
      <div className="row g-2 mb-3">
        <div className="col-md-6">
          <label className="form-label fw-semibold">Type</label>
          <select
            className="form-select"
            value={type}
            onChange={(e) => setType(e.target.value as typeof type)}
          >
            <option value="for-companies">for-companies</option>
            <option value="for-candidates">for-candidates</option>
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label fw-semibold">Icon</label>
          <input className="form-control" value={icon} onChange={(e) => setIcon(e.target.value)} />
        </div>
      </div>
      <ImageUploadField label="Image" currentPath={imagePath} onPick={setImageFile} />
      <button className="btn btn-success" disabled={saving} type="submit">
        {saving ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}
